import React, { useCallback, useEffect, useState } from "react";
import { appConfigService } from "services/appConfigService";
import { LocaleContext } from "./context";
import { Button, ConfigProvider, Result } from "antd";
import viVN from "antd/lib/locale/vi_VN";
import enUS from "antd/lib/locale/en_US";
import i18n from "i18next";
import eventService, { AppEvent } from "appEvents";
import { useInitLocale } from "./hooks";

interface Props {
  onLoaded?: () => void;
}

const LocaleProvider: React.FC<Props> = ({ children, onLoaded }) => {
  const [init, loading, error] = useInitLocale();

  useEffect(() => {
    init().finally(onLoaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init]);

  const [language, setLanguage] = useState<"en" | "vi">(
    appConfigService.getItem("lang") || "en"
  );
  const [locale, setLocale] = useState(language === "vi" ? viVN : enUS);
  const handleLanguageChange = useCallback(
    (lang: "en" | "vi") => {
      i18n.changeLanguage(lang).then(() => {
        eventService.emit(AppEvent.CHANGE_LANGUAGE, lang);
        setLanguage(lang);
        setLocale(lang === "vi" ? viVN : enUS);
      });
    },
    [setLanguage, setLocale]
  );

  if (error) {
    return (
      <Result
        status={500}
        title="Server Error"
        subTitle={
          error?.message ||
          error?.error ||
          "Something went wrong, please try again later!"
        }
        extra={[
          <Button
            loading={loading}
            disabled={loading}
            type="primary"
            onClick={init}
            key="try_again"
          >
            Try Again
          </Button>,
          <Button onClick={() => window.history.back()} key="go_back">
            Go Back
          </Button>,
        ]}
      />
    );
  }

  return (
    <LocaleContext.Provider
      value={{
        language,
        setLanguage: handleLanguageChange,
        locale: language === "vi" ? "vi-VN" : "en-US",
      }}
    >
      <ConfigProvider locale={locale}>{children}</ConfigProvider>
    </LocaleContext.Provider>
  );
};

export default LocaleProvider;
